import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, GraduationCap, ShieldCheck, Compass, HeartHandshake, Users, MessageSquareHeart, HandHeart } from "lucide-react";
import ValuesRow from "@/components/ValuesRow";

const services = [
  { num: "01", icon: GraduationCap, title: "Early Intervention", blurb: "Mentoring, life skills and community connection for people at risk of justice involvement." },
  { num: "02", icon: ShieldCheck, title: "Justice Support", blurb: "Face-to-face prison visits, advocacy, and reintegration planning during involvement." },
  { num: "03", icon: Compass, title: "Reintegration", blurb: "Housing, employment, Centrelink and community connection after release." },
  { num: "04", icon: HeartHandshake, title: "Family & Carer Support", blurb: "Support, education and advocacy for the loved ones who hold everything together." },
  { num: "05", icon: Users, title: "Social Work Services", blurb: "Counselling, trauma-informed care, case management and safety planning." },
  { num: "06", icon: HandHeart, title: "Advocacy", blurb: "Navigation, communication and representation across the systems people encounter." },
  { num: "07", icon: MessageSquareHeart, title: "Peer Support", blurb: "Real change, walked out by workers with lived experience of the system." },
];

export default function Home() {
  return (
    <div data-testid="home-page" className="fade-in">
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-16 lg:pt-24 pb-12 lg:pb-20">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            {/* Left — big display type */}
            <div className="lg:col-span-8 relative">
              <div className="njas-eyebrow" data-testid="hero-eyebrow">A not-for-profit walking with people</div>

              <div className="mt-6 relative">
                <h1
                  className="njas-display text-[18vw] sm:text-[13vw] lg:text-[10.5vw] leading-[0.85] text-njas-navy"
                  data-testid="hero-heading"
                >
                  NOT&nbsp;JUST<br/>
                  A&nbsp;STATISTIC.
                </h1>
              </div>

              <p className="mt-10 max-w-xl text-lg text-njas-muted leading-relaxed">
                We support people affected by the justice system, from early intervention through incarceration and back into community. Trauma informed. Person centred. Without judgement.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/refer" data-testid="hero-refer-cta" className="njas-condensed tracking-[0.22em] text-[13px] font-semibold px-7 py-4 bg-njas-navy text-njas-bg hover:bg-njas-navy-hover transition-colors inline-flex items-center gap-3">
                  Make a Referral <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to="/services" data-testid="hero-services-cta" className="njas-condensed tracking-[0.22em] text-[13px] font-semibold px-7 py-4 border border-njas-navy text-njas-navy hover:bg-njas-navy hover:text-njas-bg transition-colors">
                  Explore Services
                </Link>
              </div>
            </div>

            {/* Right — supporting column with tagline stack */}
            <aside className="lg:col-span-4 lg:pl-8 lg:border-l lg:border-[var(--njas-sage)]/40 space-y-8">
              <div className="relative pt-2" data-testid="tagline-stack">
                <div className="njas-display text-[54px] sm:text-[64px] leading-[0.85] text-njas-navy">Elevate.</div>
                <div className="njas-script text-[68px] sm:text-[82px] leading-[0.7] text-njas-sage-hover pl-10 sm:pl-14 -mt-1 italic">Empower.</div>
                <div
                  className="njas-display text-[54px] sm:text-[64px] leading-[0.85] mt-1"
                  style={{ WebkitTextStroke: "1.5px #12283F", color: "transparent" }}
                >
                  Belong.
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <span className="w-8 h-px bg-njas-sage" />
                  <span className="njas-condensed tracking-[0.3em] text-[10px] font-semibold text-njas-sage-deep">Our promise, in three words</span>
                </div>
              </div>
              <div className="njas-h-rule" />
              <div>
                <div className="njas-display text-4xl text-njas-navy">12<span className="text-njas-sage">+</span></div>
                <div className="njas-value-label mt-2">Years combined social-work experience</div>
              </div>
              <div>
                <div className="njas-display text-4xl text-njas-navy">07</div>
                <div className="njas-value-label mt-2">Pillars of support</div>
              </div>
            </aside>
          </div>
        </div>

        {/* Values row — the sage circle badges from the signature */}
        <div className="border-t border-[var(--njas-line)] bg-[var(--njas-bg-alt)]">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12">
            <ValuesRow />
          </div>
        </div>
      </section>

      {/* ============ MISSION ============ */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="njas-eyebrow">Our Mission</div>
            <div className="mt-6 njas-script text-4xl text-njas-sage-deep">No one should be defined</div>
            <h2 className="njas-display text-6xl sm:text-7xl text-njas-navy mt-2 leading-[0.9]">
              BY THEIR<br/>PAST.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 lg:pl-10 lg:border-l lg:border-[var(--njas-sage)]/40 text-lg text-njas-text leading-relaxed space-y-5">
            <p>
              We work to prevent justice system involvement through early intervention, support
              people during incarceration, and walk alongside them as they rebuild their lives in the community.
            </p>
            <p>
              Through advocacy, mentoring, trauma informed care, counselling, case management and practical
              support, we empower people to build lives filled with purpose, opportunity and hope.
            </p>
            <p>
              And we know that lasting change extends beyond the individual. By supporting families,
              carers and loved ones, we help create the foundation for successful reintegration and
              healthier communities.
            </p>
            <Link to="/about" className="njas-link njas-condensed tracking-[0.22em] font-semibold text-[13px] inline-block mt-3">
              Read our full approach →
            </Link>
          </div>
        </div>
      </section>

      {/* ============ CINEMATIC IMAGE STRIP ============ */}
      <section className="border-t border-[var(--njas-line)]" data-testid="cinematic-strip">
        <div className="relative h-[52vh] min-h-[400px] max-h-[720px] overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1773356884180-aaf4ec4987a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxob3BlZnVsJTIwcG9ydHJhaXQlMjBzdW5saWdodCUyMG91dGRvb3JzfGVufDB8fHx8MTc4MzI1Mzg1M3ww&ixlib=rb-4.1.0&q=85"
            alt="Community, hope, sunlight"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-njas-navy/70" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pb-14 lg:pb-20 w-full">
              <div className="njas-eyebrow text-njas-sage">A Moment</div>
              <h3 className="njas-display text-5xl sm:text-7xl text-njas-bg mt-3 leading-[0.9] max-w-3xl">
                DIGNITY IS<br/>THE STARTING POINT.
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SERVICES — hairline list ============ */}
      <section className="bg-njas-bg border-y border-[var(--njas-line)]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-28">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div>
              <div className="njas-eyebrow">What We Do</div>
              <h2 className="njas-display text-6xl sm:text-7xl text-njas-navy mt-3 leading-[0.9]">
                SUPPORT ACROSS<br/>EVERY STAGE.
              </h2>
            </div>
            <p className="max-w-md text-njas-muted">
              We meet people where they are, before the system, during it, and long after. Every program is delivered by qualified social workers alongside peer workers with lived experience.
            </p>
          </div>

          <div className="border-t border-[var(--njas-sage)]/50">
            {services.map(({ num, icon: Icon, title, blurb }) => (
              <Link
                key={title}
                to="/services"
                data-testid={`service-row-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group grid grid-cols-12 gap-4 items-center py-8 border-b border-[var(--njas-sage)]/50 hover:bg-[var(--njas-bg-alt)] transition-colors -mx-4 px-4"
              >
                <div className="col-span-2 sm:col-span-1">
                  <span className="njas-display text-3xl text-njas-sage">{num}</span>
                </div>
                <div className="col-span-1 hidden sm:block">
                  <div className="njas-badge-outline"><Icon className="w-5 h-5" /></div>
                </div>
                <div className="col-span-10 sm:col-span-6">
                  <div className="njas-display text-3xl sm:text-4xl text-njas-navy group-hover:text-njas-sage-deep transition-colors">{title}</div>
                </div>
                <div className="col-span-11 sm:col-span-3 text-sm text-njas-muted col-start-2 sm:col-start-auto">{blurb}</div>
                <div className="col-span-1 flex justify-end">
                  <ArrowUpRight className="w-5 h-5 text-njas-navy group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ APPROACH — big statement ============ */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <div className="njas-eyebrow">Our Approach</div>
            <h2 className="njas-display text-6xl sm:text-7xl lg:text-8xl text-njas-navy mt-4 leading-[0.9]">
              WE'RE&nbsp;HERE<br/>FOR&nbsp;CLIENTS.
            </h2>
            <div className="njas-script text-5xl text-njas-sage-deep mt-4">not the system.</div>
          </div>
          <div className="lg:col-span-6 space-y-8">
            {[
              { k: "01", t: "Dignity, first.", c: "Every person is treated with respect. No judgement. No labels. Ever." },
              { k: "02", t: "Person centred.", c: "Every plan is built with you, around your goals, your pace, your story." },
              { k: "03", t: "Trauma informed.", c: "We understand that people carry hard experiences, and we hold that with care." },
              { k: "04", t: "Family included.", c: "Justice involvement affects entire families. We support the people around you too." },
            ].map((p) => (
              <div key={p.k} className="grid grid-cols-12 gap-4 pb-6 border-b border-[var(--njas-line)]">
                <span className="col-span-2 njas-display text-2xl text-njas-sage">{p.k}</span>
                <div className="col-span-10">
                  <div className="njas-display text-2xl text-njas-navy">{p.t}</div>
                  <div className="text-njas-muted text-sm mt-2 leading-relaxed">{p.c}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CTA STRIP ============ */}
      <section className="bg-njas-navy text-njas-bg">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid md:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-njas-sage/30">
            <CTA testid="cta-refer" tag="01 · Refer" title="Make a referral" copy="Refer yourself, a family member, or a client. Confidential and free." to="/refer" />
            <CTA testid="cta-volunteer" tag="02 · Volunteer" title="Walk with us" copy="Peer workers, mentors and volunteers with lived experience welcome." to="/get-involved" />
            <CTA testid="cta-donate" tag="03 · Donate" title="Fund the work" copy="Every dollar goes to service delivery. Online giving is arriving soon." to="/get-involved#donate" />
          </div>
        </div>
      </section>
    </div>
  );
}

function PartnerCell({ num, href, testid, logoSrc, alt, logoScale = 1, tag }) {
  return (
    <a
      href={href}
      data-testid={testid}
      className="group relative flex items-center justify-center px-8 py-14 min-h-[220px] hover:bg-[var(--njas-bg-alt)]/50 transition-colors"
    >
      <img
        src={logoSrc}
        alt={alt}
        className="max-h-[130px] max-w-[80%] object-contain transition-transform duration-300 group-hover:scale-[1.03]"
        style={{ transform: `scale(${logoScale})` }}
      />
    </a>
  );
}

function CTA({ tag, title, copy, to, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group block py-10 md:py-4 md:px-8 first:md:pl-0 last:md:pr-0 hover:bg-white/5 transition-colors"
    >
      <div className="njas-eyebrow text-njas-sage">{tag}</div>
      <div className="njas-display text-4xl mt-4">{title}</div>
      <p className="text-njas-bg/70 mt-4 text-sm leading-relaxed">{copy}</p>
      <div className="mt-6 inline-flex items-center gap-2 njas-condensed tracking-[0.22em] text-[12px] font-semibold text-njas-sage group-hover:gap-3 transition-all">
        Take me there <ArrowUpRight className="w-4 h-4" />
      </div>
    </Link>
  );
}
